import React, { useState, useEffect } from 'react';
import { Body, Box, Heading, SimpleGrid, Icon } from '@hover/blueprint';
import { iCheck, iChevronLeft, iChevronRight, iX } from '@hover/icons';
import Slider from 'react-slick';
import { nanoid } from 'nanoid';
import styled from '@emotion/styled';
import {
  PRICING_TABLE_TABLET_AND_MOBILE,
  PRICING_PAGE_TABLE_HEADER,
  PRICING_TABLE_DATA,
} from '../../../constants';
import {
  IPricingFeature,
  IPricingSection,
} from '../../../interfaces/IPricingPageTableData';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import HeaderCard from './HeaderCard';
import { useSyncTableHeaderAndTableData } from '../../../hooks';
import PricingTooltip from '../plans-and-pricing/PricingTooltip';
import { DownloadAppModal } from '../../common';

const MobileSlider = styled(Slider)`
  .slick-list {
    max-height: 100% !important;
  }
`;

const TabletTable: React.FC = () => {
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerHeight : 0,
  );
  const [isOpen, setOpen] = useState<boolean>(false);
  const toggleModal = () => setOpen(!isOpen);

  useEffect(() => {
    setWindowWidth(window.innerWidth);
    const onWindowSizeChange = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onWindowSizeChange);
    return () => window.removeEventListener('resize', onWindowSizeChange);
  }, []);

  const getTabletMargins = (index: number): string => {
    if (index === 22 && windowWidth < 975 && typeof window !== 'undefined') {
      return '144px';
    }
    return [12, 17, 22].includes(index) ? '108px' : '0';
  };

  const getMobileMargins = (index: number): string => {
    if (typeof window !== 'undefined') {
      if (index === 22 && windowWidth <= 974 && windowWidth > 935) {
        return '108px !important';
      }

      if (index === 17 && windowWidth <= 379) {
        return '80px';
      }

      if (index === 22 && windowWidth <= 422 && windowWidth > 409) {
        return '104px';
      }

      if (index === 12 && windowWidth <= 442 && windowWidth > 435) {
        return '80px';
      }

      if (index === 22 && windowWidth < 425) {
        return '128px';
      }

      if (
        (index === 22 && windowWidth < 634) ||
        (index === 12 && windowWidth < 444) ||
        (index === 17 && windowWidth < 380)
      ) {
        return '104px';
      }
    }
    return [12, 17, 22].includes(index) ? '80px' : '0';
  };

  const getHeight = (index: number): string | number => {
    if (index === 22 && typeof window !== 'undefined') {
      if (windowWidth < 424) {
        return '73px';
      }
      if (windowWidth <= 425) {
        return '73px';
      }
    }
    return [12, 17, 22].includes(index) ? '65px' : 500;
  };

  const { settings, onNext, onPrev, headerRef, tableRef } =
    useSyncTableHeaderAndTableData();

  return (
    <SimpleGrid
      pb="80px"
      columns={2}
      spacing={0}
      d={{ base: 'grid', desktop: 'none' }}
    >
      <DownloadAppModal isOpen={isOpen} onClose={toggleModal} />
      <Box
        pt={{ base: '211px', tablet: '270px' }}
        pl={{ base: 500, tablet: '56px' }}
        flexDir="column"
        shadow="distance300"
      >
        {PRICING_TABLE_DATA.map((data: IPricingSection, index) => (
          <Box flexDir="column" key={data.sectionName}>
            <Box borderBottomWidth="1px" borderColor="neutral.400">
              <Heading
                pr={400}
                mt={{
                  base: index === 0 ? 0 : 700,
                  tablet: index === 0 ? 0 : 800,
                }}
                marginBottom={{ base: 400, tablet: 500 }}
                size={{ base: 200, tablet: 500 }}
                fontWeight={700}
              >
                {data.sectionName}
              </Heading>
            </Box>
            {data.features.map((feature: IPricingFeature) => (
              <Box
                key={feature.feature}
                minH={500}
                w="100%"
                borderBottomWidth="1px"
                borderColor="neutral.400"
                alignItems="center"
              >
                <PricingTooltip
                  maxW="300px"
                  pr={400}
                  pl={{ base: 0, tablet: 600 }}
                  tooltip={{
                    id: nanoid(),
                    title: feature.feature,
                    tooltip: feature.tooltip,
                  }}
                />
              </Box>
            ))}
          </Box>
        ))}
      </Box>
      <Box flexDir="column" position="relative">
        <Box
          position={{ base: 'absolute', tablet: 'static' }}
          pr={{ base: 500, tablet: '56px' }}
          justifyContent="space-between"
          alignItems="center"
          mb={500}
          pl={400}
          top={{ base: '-4px', tablet: 0 }}
          left={0}
          w="100%"
        >
          <Icon
            color="primary.600"
            strokeWidth="2px"
            icon={iChevronLeft}
            size="default"
            m={0}
            zIndex={10}
            onClick={onPrev}
          />
          <Icon
            zIndex={10}
            onClick={onNext}
            strokeWidth="2px"
            color="primary.600"
            icon={iChevronRight}
            size="default"
            m={0}
          />
        </Box>
        <Box
          d="block"
          pl={400}
          pr={{ base: 500, tablet: '56px' }}
          mb={{ base: '47px', tablet: '66px' }}
        >
          <MobileSlider ref={headerRef} {...settings}>
            {PRICING_PAGE_TABLE_HEADER.map(header => (
              <Box
                key={header.id}
                px={{ base: 0, tablet: '6px' }}
                flexDir="column"
                alignItems="center"
                pb={300}
              >
                <Body
                  mb={400}
                  textAlign="center"
                  size={{ base: 300, tablet: 600 }}
                  isBold
                  color="neutral.300"
                >
                  {header.type}
                </Body>
                {/* TODO(links): pricing modal */}
                <HeaderCard card={header} onLinkClick={toggleModal} />
              </Box>
            ))}
          </MobileSlider>
        </Box>
        <Box d="block" borderTopWidth="1px" borderColor="neutral.400">
          <MobileSlider ref={tableRef} {...settings}>
            {PRICING_TABLE_TABLET_AND_MOBILE.map(column => (
              <Box key={column.id}>
                {column.values.map((value, index) => (
                  <Box
                    key={nanoid()}
                    borderBottomWidth="1px"
                    borderColor="neutral.400"
                    borderTopWidth={[13, 18, 23].includes(index) ? '1px' : 0}
                    h={getHeight(index)}
                    mb={{
                      base: getMobileMargins(index),
                      tablet: getTabletMargins(index),
                    }}
                    alignItems="center"
                  >
                    {typeof value === 'string' ? (
                      <Body textAlign="center" mx="auto" size={400} maxW="80%">
                        {value}
                      </Body>
                    ) : (
                      <Icon
                        icon={value ? iCheck : iX}
                        size="default"
                        mx="auto"
                        color={value ? 'success.600' : 'brandOrange.300'}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            ))}
          </MobileSlider>
        </Box>
      </Box>
    </SimpleGrid>
  );
};

export default TabletTable;
